if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.buyButtonsObserver = null;
        this.footerObserver = null;
        this.submitObserver = null;
        this.isStuck = false;
        this.hiddenByBottom = false;
        this.pendingAdd = false;
        this.addedTimeout = null;
        this.unsubscribers = [];
        this.onQuantityChange = this.handleQuantityChange.bind(this);
        this.onStickyButtonClick = this.handleAddToCartClick.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.stickyBar = this.querySelector(`#StickyBar-${this.sectionId}`);
        this.stickyButton = this.querySelector(`#StickyAddToCartButton-${this.sectionId}`);
        this.quantityDisplay = this.querySelector(`#StickyQuantity-${this.sectionId}`);
        this.addedLabel = this.querySelector('.sticky-add-to-cart__added');
        this.buttonLabel = this.querySelector('.sticky-add-to-cart__button-label');
        this.sectionEl = this.closest('.shopify-section');

        if (!this.stickyBar || !this.stickyButton) return;

        this.stickyButton.addEventListener('click', this.onStickyButtonClick);
        this.bindQuantityInput();
        this.observeSubmitButton();
        this.setupIntersectionObservers();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this)));
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this)));
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartError, this.handleCartError.bind(this)));
        }
      }

      disconnectedCallback() {
        this.buyButtonsObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.submitObserver?.disconnect();
        this.stickyButton?.removeEventListener('click', this.onStickyButtonClick);
        this.quantityInput?.removeEventListener('input', this.onQuantityChange);
        this.quantityInput?.removeEventListener('change', this.onQuantityChange);
        this.unsubscribers.forEach((unsubscribe) => unsubscribe?.());
        this.unsubscribers = [];
        if (this.addedTimeout) clearTimeout(this.addedTimeout);
      }

      get productForm() {
        return this.sectionEl?.querySelector('product-form');
      }

      get submitButton() {
        return this.sectionEl?.querySelector(`#product-form-${this.sectionId}-submit`);
      }

      observeSubmitButton() {
        const submitButton = this.submitButton;
        if (!submitButton) return;

        this.submitObserver = new MutationObserver(() => {
          this.syncFromLiveSubmitButton();
        });
        this.submitObserver.observe(submitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
        });
      }

      bindQuantityInput() {
        this.quantityInput = this.sectionEl?.querySelector(`#Quantity-${this.sectionId}`);
        if (!this.quantityInput) return;
        this.quantityInput.addEventListener('input', this.onQuantityChange);
        this.quantityInput.addEventListener('change', this.onQuantityChange);
        this.updateQuantityDisplay();
      }

      setupIntersectionObservers() {
        const buyButtons = this.productForm;
        const footer = document.querySelector('footer') || document.querySelector('.shopify-section-group-footer-group');
        if (!buyButtons || !footer) return;

        this.buyButtonsObserver = new IntersectionObserver((entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (!entry.isIntersecting && !this.isStuck && this.buyButtonsAreAboveViewport()) {
            this.showStickyBar();
          } else if (entry.isIntersecting && this.isStuck) {
            this.hideStickyBar();
          }
        });

        this.footerObserver = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            if (!entry) return;

            if (entry.isIntersecting) {
              this.hiddenByBottom = true;
              if (this.isStuck) this.hideStickyBar();
            } else if (this.hiddenByBottom) {
              this.hiddenByBottom = false;
              if (this.buyButtonsAreAboveViewport()) this.showStickyBar();
            }
          },
          { rootMargin: '0px 0px 200px 0px' }
        );

        this.buyButtonsObserver.observe(buyButtons);
        this.footerObserver.observe(footer);
      }

      handleAddToCartClick() {
        const submitButton = this.submitButton;
        if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

        this.pendingAdd = true;
        submitButton.click();
      }

      handleVariantChange(event) {
        const data = event?.data;
        if (!data || data.sectionId !== this.sectionId || !data.html) return;

        const nextRoot = data.html.querySelector('sticky-add-to-cart');
        if (!nextRoot) return;

        this.dataset.variantAvailable = nextRoot.dataset.variantAvailable || String(Boolean(data.variant?.available));
        this.replaceNode(`#StickyImage-${this.sectionId}`, nextRoot);
        this.replaceNode(`#StickyInfo-${this.sectionId}`, nextRoot);
        this.replaceNode(`#StickyPrice-${this.sectionId}`, nextRoot);

        const nextButton = nextRoot.querySelector(`#StickyAddToCartButton-${this.sectionId}`);
        if (nextButton && this.stickyButton) {
          this.stickyButton.disabled = nextButton.disabled;
          this.stickyButton.toggleAttribute('aria-disabled', nextButton.hasAttribute('aria-disabled') || nextButton.disabled);
          const nextLabel = nextButton.querySelector('.sticky-add-to-cart__button-label');
          if (nextLabel && this.buttonLabel) {
            this.buttonLabel.innerHTML = nextLabel.innerHTML;
            this.quantityDisplay = this.querySelector(`#StickyQuantity-${this.sectionId}`);
          }
        }

        this.syncFromLiveSubmitButton();
        this.updateQuantityDisplay();

        if (this.dataset.variantAvailable !== 'true') {
          this.hideStickyBar();
        } else if (this.buyButtonsAreAboveViewport()) {
          this.showStickyBar();
        }
      }

      buyButtonsAreAboveViewport() {
        const buyButtons = this.productForm;
        if (!buyButtons) return false;
        const rect = buyButtons.getBoundingClientRect();
        return rect.bottom < 0;
      }

      replaceNode(selector, sourceRoot) {
        const destination = this.querySelector(selector);
        const source = sourceRoot.querySelector(selector);
        if (!destination || !source) return;
        destination.replaceWith(source.cloneNode(true));
      }

      handleQuantityChange() {
        this.updateQuantityDisplay();
      }

      updateQuantityDisplay() {
        this.quantityDisplay = this.querySelector(`#StickyQuantity-${this.sectionId}`);
        if (!this.quantityDisplay) return;

        const quantity = parseInt(this.quantityInput?.value || '1', 10) || 1;
        this.quantityDisplay.textContent = `(${quantity})`;

        const available = this.dataset.variantAvailable === 'true' && !this.stickyButton?.disabled;
        this.quantityDisplay.style.display = available && quantity > 1 ? 'inline' : 'none';
      }

      handleCartUpdate(event) {
        if (event?.source !== 'product-form' || !this.pendingAdd) return;
        this.pendingAdd = false;
        this.setAddedState(true);
        if (this.addedTimeout) clearTimeout(this.addedTimeout);
        this.addedTimeout = setTimeout(() => this.setAddedState(false), 800);
      }

      handleCartError(event) {
        if (event?.source !== 'product-form') return;
        this.pendingAdd = false;
        this.setAddedState(false);
      }

      setAddedState(isAdded) {
        if (!this.stickyButton) return;
        this.stickyButton.toggleAttribute('data-added', isAdded);
        if (this.addedLabel) this.addedLabel.hidden = !isAdded;
        if (this.buttonLabel) this.buttonLabel.hidden = isAdded;
      }

      syncFromLiveSubmitButton() {
        const submitButton = this.submitButton;
        if (!submitButton || !this.stickyButton) return;
        const disabled = submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true';
        this.stickyButton.disabled = disabled;
        this.stickyButton.toggleAttribute('aria-disabled', disabled);
      }

      showStickyBar() {
        if (this.hiddenByBottom) return;
        if (this.dataset.variantAvailable !== 'true') return;
        this.syncFromLiveSubmitButton();
        if (this.stickyButton?.disabled) return;
        this.isStuck = true;
        this.stickyBar.dataset.stuck = 'true';
      }

      hideStickyBar() {
        this.isStuck = false;
        this.stickyBar.dataset.stuck = 'false';
      }
    }
  );
}
